import React from 'react';
import { Modal, Button, Typography, Select, Segmented, theme } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import { AnimatePresence, motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { IServiceSearchFilters } from '../interfaces/heavyDutyMachinery/IService';
import { SERVICE_CATEGORY_OPTIONS, SERVICE_CATEGORY_SECTORS } from '../constants/serviceOptions';
import { TRANSLATIONS } from '../constants/translations';
import { translate } from '../helpers/translationHelper';
import { useAppBreakpoint } from '../hooks/useAppBreakpoint';

const { Text } = Typography;

interface ServiceFiltersModalProps {
  open: boolean;
  onClose: () => void;
  filters: IServiceSearchFilters;
  totalCount: number;
  onChange: (patch: Partial<IServiceSearchFilters>) => void;
  onClear: () => void;
}

/**
 * The Services tab's filters overlay -- same theme, layout and
 * mobile-fullscreen/desktop-Modal fork as every other filters modal here,
 * with the three fields a service actually has.
 *
 * **No price range, and that is deliberate.** A service is priced either by
 * the hour or as a whole closed job, and putting both behind one slider makes
 * "between 20 and 60" mean two unrelated things -- while filtering on €/h
 * alone would silently hide everybody who works to a fixed price, which is
 * worse than not filtering at all. "Por horas o precio cerrado" below is the
 * honest version of the same question. Two separate, labelled controls would
 * work; one shared slider would not.
 */
const ServiceFiltersModal: React.FC<ServiceFiltersModalProps> = ({ open, onClose, filters, totalCount, onChange, onClear }) => {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const { isMobile } = useAppBreakpoint();

  // Grouped by sector, exactly as the provider's own create form groups them
  // -- fifty-nine trades flat is a scroll, and a searchable Select with sector
  // headers is what somebody looking for "electricista" actually needs. The
  // original four categories carry no sector and render ungrouped on top.
  const categoryOptions = [
    ...SERVICE_CATEGORY_OPTIONS.filter((option) => !option.sectorKey).map((option) => ({
      value: option.id,
      label: translate(t, option.labelKey),
    })),
    ...SERVICE_CATEGORY_SECTORS.map((sector) => ({
      label: translate(t, sector.labelKey),
      options: SERVICE_CATEGORY_OPTIONS.filter((option) => option.sectorKey === sector.key).map((option) => ({
        value: option.id,
        label: translate(t, option.labelKey),
      })),
    })),
  ];

  const renderCategory = () => (
    <div style={{ marginBottom: 20 }}>
      <Text strong style={{ display: 'block', marginBottom: 8 }}>
        {translate(t, TRANSLATIONS.SERVICE_FILTERS_CATEGORY_LABEL)}
      </Text>
      <Select
        allowClear
        showSearch
        optionFilterProp="label"
        style={{ width: '100%' }}
        placeholder={translate(t, TRANSLATIONS.SERVICE_FILTERS_CATEGORY_PLACEHOLDER)}
        value={filters.serviceCategoryId}
        options={categoryOptions}
        onChange={(value) => onChange({ serviceCategoryId: value ?? undefined, page: 1 })}
      />
    </div>
  );

  // Segmented rather than a Select for both of these: three options, and the
  // "Todos" option has to be visible rather than hidden behind a clear button
  // -- a guest who narrowed to "presupuesto" and cannot see how to go back
  // concludes the map is broken.
  const renderSegmented = (
    labelKey: string,
    hintKey: string | undefined,
    value: string | undefined,
    options: { value: string; labelKey: string }[],
    apply: (next?: string) => void
  ) => (
    <div style={{ marginBottom: 20 }}>
      <Text strong style={{ display: 'block', marginBottom: 8 }}>
        {translate(t, labelKey)}
      </Text>
      <Segmented
        block
        value={value ?? 'all'}
        onChange={(next) => apply(next === 'all' ? undefined : String(next))}
        options={[
          { value: 'all', label: translate(t, TRANSLATIONS.SERVICE_FILTERS_ANY) },
          ...options.map((option) => ({ value: option.value, label: translate(t, option.labelKey) })),
        ]}
      />
      {hintKey && (
        <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 6 }}>
          {translate(t, hintKey)}
        </Text>
      )}
    </div>
  );

  const body = (
    <>
      {renderCategory()}
      {renderSegmented(
        TRANSLATIONS.SERVICE_FILTERS_REQUEST_MODE_LABEL,
        TRANSLATIONS.SERVICE_FILTERS_REQUEST_MODE_HINT,
        filters.requestMode,
        [
          { value: 'Instant', labelKey: TRANSLATIONS.SERVICE_FILTERS_REQUEST_MODE_INSTANT },
          { value: 'Quote', labelKey: TRANSLATIONS.SERVICE_FILTERS_REQUEST_MODE_QUOTE },
        ],
        (next) => onChange({ requestMode: next, page: 1 })
      )}
      {renderSegmented(
        TRANSLATIONS.SERVICE_FILTERS_PRICING_MODE_LABEL,
        undefined,
        filters.pricingMode,
        [
          { value: 'Hourly', labelKey: TRANSLATIONS.HEAVY_DUTY_MACHINERY_OPT_PRICING_MODE_HOURLY },
          { value: 'FixedJob', labelKey: TRANSLATIONS.HEAVY_DUTY_MACHINERY_OPT_PRICING_MODE_FIXED_JOB },
        ],
        (next) => onChange({ pricingMode: next, page: 1 })
      )}
    </>
  );

  const renderShowResultsButton = (style?: React.CSSProperties) => (
    <Button type="primary" onClick={onClose} style={{ borderRadius: 999, ...style }}>
      {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SEARCH_SHOW_RESULTS_BUTTON)} ({totalCount})
    </Button>
  );

  const renderClearFiltersButton = () => (
    <button
      type="button"
      onClick={onClear}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        minHeight: 44,
        padding: '0 4px',
        marginLeft: -4,
        textDecoration: 'underline',
        color: token.colorTextSecondary,
        cursor: 'pointer',
        background: 'none',
        border: 'none',
        font: 'inherit',
      }}
    >
      {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SEARCH_CLEAR_FILTERS)}
    </button>
  );

  if (isMobile) {
    return (
      <AnimatePresence>
        {open && (
          <motion.div
            key="service-filters-overlay"
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ duration: 0.25, ease: 'easeInOut' }}
            style={{ position: 'fixed', inset: 0, zIndex: 2000, background: token.colorBgContainer, display: 'flex', flexDirection: 'column' }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', flexShrink: 0 }}>
              <Text strong style={{ fontSize: 16 }}>
                {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SEARCH_FILTERS_TITLE)}
              </Text>
              <button
                type="button"
                aria-label={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SEARCH_FILTERS_OVERLAY_CLOSE)}
                onClick={onClose}
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: '50%',
                  border: 'none',
                  background: token.colorFillTertiary,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  flexShrink: 0,
                }}
              >
                <CloseOutlined style={{ fontSize: 16 }} />
              </button>
            </div>

            <div className="no-scrollbar" style={{ flex: 1, overflowY: 'auto', padding: '0 20px', scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
              {body}
            </div>

            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 12,
                padding: '16px 20px',
                paddingBottom: 'calc(16px + env(safe-area-inset-bottom, 0px))',
                borderTop: `1px solid ${token.colorBorderSecondary}`,
                flexShrink: 0,
              }}
            >
              {renderClearFiltersButton()}
              {renderShowResultsButton()}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    );
  }

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width="min(760px, 92vw)"
      transitionName="filters-slide"
      maskTransitionName="filters-fade"
      title={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SEARCH_FILTERS_TITLE)}
      classNames={{ body: 'no-scrollbar' }}
      styles={{
        content: { borderRadius: 20, border: '0.5px solid rgba(0,0,0,0.08)', boxShadow: '0 8px 24px rgba(0,0,0,0.08)', overflow: 'hidden' },
        body: { maxHeight: '65vh', overflowY: 'auto', padding: '0 28px', scrollbarWidth: 'none', msOverflowStyle: 'none' },
      }}
    >
      {body}

      <div
        style={{
          position: 'sticky',
          bottom: 0,
          background: token.colorBgContainer,
          margin: '16px -28px 0',
          padding: '16px 28px',
          borderTop: `1px solid ${token.colorBorderSecondary}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        {renderClearFiltersButton()}
        {renderShowResultsButton()}
      </div>
    </Modal>
  );
};

export default ServiceFiltersModal;
